// Every call to the API goes through here.
//
// ONE ERROR SHAPE. The API answers every failure with `{error, message}` and
// sometimes `details`, on every route and every error path, so this turns all
// of them into one exception carrying all three and the screens render it. The
// message is written to be shown, and an error swallowed reads as a broken
// application rather than as whatever the API said was wrong.

import { config } from './config';
import { apiToken } from './auth';
import type {
  Aggregate,
  BarrierViewName,
  BarrierViewPage,
  BarrierViewRow,
  ByAction,
  ByCommodity,
  ByJob,
  DirectoryPerson,
  Me,
  OutlookBuildByWeek,
  Page,
  Row,
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Per-field messages from a 422. The form renders each beside its input. */
  readonly details: Record<string, string> | null;

  constructor(status: number, code: string, message: string, details: Record<string, string> | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

function queryString(params: Query | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, params?: Query): Promise<T> {
  const token = await apiToken();
  const response = await fetch(`${config().apiBaseUrl}${path}${queryString(params)}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      String(parsed.error ?? 'error'),
      String(parsed.message ?? `The request failed with ${response.status}.`),
      (parsed.details as Record<string, string> | undefined) ?? null
    );
  }
  return parsed as T;
}

// ---- the collections -------------------------------------------------

export const listRows = (slug: string, params?: Query) => request<Page<Row>>('GET', `/api/${slug}`, undefined, params);
export const getRow = (slug: string, id: number | string) => request<Row>('GET', `/api/${slug}/${id}`);
export const createRow = (slug: string, body: Row) => request<Row>('POST', `/api/${slug}`, body);
export const updateRow = (slug: string, id: number | string, body: Row) =>
  request<Row>('PATCH', `/api/${slug}/${id}`, body);
export const deleteRow = (slug: string, id: number | string) => request<void>('DELETE', `/api/${slug}/${id}`);

// ---- the four views --------------------------------------------------

export const barrierView = (view: BarrierViewName, params?: Query) =>
  request<BarrierViewPage>('GET', `/api/views/barriers/${view}`, undefined, params);

// ---- the aggregates --------------------------------------------------

export const barriersByAction = () =>
  request<Aggregate<ByAction>>('GET', '/api/aggregates/barriers/by-action');
export const barriersByCommodity = () =>
  request<Aggregate<ByCommodity>>('GET', '/api/aggregates/barriers/by-commodity');
export const barriersByJob = () => request<Aggregate<ByJob>>('GET', '/api/aggregates/barriers/by-job');
export const outlookBuildByWeek = () =>
  request<Aggregate<OutlookBuildByWeek>>('GET', '/api/aggregates/two-week-production/outlook-build-by-week');

export const me = () => request<Me>('GET', '/api/me');

// ---- the directory ---------------------------------------------------

/** People in Entra ID whose name, mail or sign-in name matches `q`. The API
 *  asks Microsoft Graph as its own identity and wants two characters at least. */
export const searchPeople = (q: string) =>
  request<{ items: DirectoryPerson[] }>('GET', '/api/directory/users', undefined, { q }).then((r) => r.items);

/** One person, by object id -- for a grid filtered to them, whose address
 *  carries only the id. */
export const getPerson = (objectId: string) =>
  request<DirectoryPerson>('GET', `/api/directory/users/${encodeURIComponent(objectId)}`);

/** The object ids of the people in the admins group, for the Admin page. Only
 *  an Admin or an Access Manager may ask, and a 503 says why the directory
 *  could not answer rather than answering an empty list. */
export const directoryAdmins = () =>
  request<{ object_ids: string[] }>('GET', '/api/directory/admins').then((r) => r.object_ids);

// ---- paging ----------------------------------------------------------

/** The API caps a page at 1000 rows and the archive view is over 1,824 of them,
 *  so a screen that wants a whole collection asks for it a page at a time.
 *
 *  Grouping is what forces this rather than a preference for it: a grid grouped
 *  by Plant with a count on each header is wrong if it holds the first page
 *  only, and wrong in a way that looks right. The cap below is a guard against
 *  a table growing past what a browser should hold, not a limit anybody expects
 *  to reach -- the largest table in the application is the 1,824 barriers. */
const PAGE = 1000;
const MAX_ROWS = 20000;

async function pageThrough<T>(fetchPage: (offset: number) => Promise<Page<T>>): Promise<T[]> {
  const first = await fetchPage(0);
  const rows = [...first.items];
  const wanted = Math.min(first.total, MAX_ROWS);
  while (rows.length < wanted && first.items.length > 0) {
    const next = await fetchPage(rows.length);
    if (next.items.length === 0) break;
    rows.push(...next.items);
  }
  return rows;
}

export const allRows = (slug: string, params?: Query): Promise<Row[]> =>
  pageThrough((offset) => listRows(slug, { ...params, limit: PAGE, offset }));

export const allBarriers = (view: BarrierViewName, params?: Query): Promise<BarrierViewRow[]> =>
  pageThrough((offset) => barrierView(view, { ...params, limit: PAGE, offset }));
